import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Shield, Loader2 } from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { CONSENT_VERSION } from '@/constants/legalTexts';

interface ConsentDocument {
  id: number;
  document_type: string;
  version: string;
  title: string;
  content: string;
  effective_date: string;
  is_active: boolean;
  summary?: string;
}

export default function HipaaAuthorizationPage() {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [document, setDocument] = useState<ConsentDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [useApiContent, setUseApiContent] = useState(false);

  const handleBack = () => {
    // Check if there's history to go back to
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // Fallback to member home if no history
      navigate('/member/home');
    }
  };

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const response = await fetch(`${getWeaveinApiUrl()}/api/consent/documents/hipaa_authorization`);
        if (response.ok) {
          const data = await response.json();
          setDocument(data);
          // Only use API content if it's different from fallback
          setUseApiContent(data.content && data.content.length > 1000);
        }
      } catch (error) {
        console.error('Failed to fetch HIPAA document, using fallback:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDocument();
  }, []);

  const effectiveDate = document?.effective_date
    ? new Date(document.effective_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : 'January 30, 2026';

  const version = document?.version || CONSENT_VERSION;

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <Button variant="ghost" className="text-teal-700 hover:text-teal-800" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('hipaa-authorization.back', 'Back')}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                <Shield className="w-6 h-6 text-teal-700" />
              </div>
              <div>
                <CardTitle className="text-2xl">{t('hipaa-authorization.hipaa_authorization_for_use_and', 'HIPAA Authorization for Use and Disclosure of Health Information')}</CardTitle>
                <p className="text-sm text-gray-600">
                  Version {version} • Last updated: {effectiveDate}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
              </div>
            ) : useApiContent && document?.content ? (
              <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
                {document.content}
              </pre>
            ) : (
              <>
            <h2>{t('hipaa-authorization.authorization_to_use_and_disclose', 'Authorization to Use and Disclose Protected Health Information')}</h2>

            <h3>1. Purpose</h3>
            <p>
              This authorization allows Harmony Healthcare Solutions and its partnering care coordination organizations to use and disclose your protected health information (PHI) for the purposes of coordinating your care, providing services, and facilitating communication between you and your healthcare providers.
            </p>

            <h3>2. Information to Be Used or Disclosed</h3>
            <p>
              {t('hipaa-authorization.this_authorization_covers_the_following', 'This authorization covers the following types of health information:')}
            </p>
            <ul>
              <li>{t('hipaa-authorization.medical_history_and_current_health', 'Medical history and current health conditions')}</li>
              <li>{t('hipaa-authorization.treatment_plans_and_progress_notes', 'Treatment plans and progress notes')}</li>
              <li>{t('hipaa-authorization.medication_information', 'Medication information')}</li>
              <li>{t('hipaa-authorization.laboratory_and_diagnostic_test_results', 'Laboratory and diagnostic test results')}</li>
              <li>{t('hipaa-authorization.mental_health_and_substance_abuse', 'Mental health and substance abuse treatment information')}</li>
              <li>{t('hipaa-authorization.insurance_and_billing_information', 'Insurance and billing information')}</li>
              <li>{t('hipaa-authorization.referral_information_and_care_coordination', 'Referral information and care coordination notes')}</li>
              <li>{t('hipaa-authorization.social_service_needs_assessments', 'Social service needs assessments')}</li>
              <li>{t('hipaa-authorization.contact_information_and_preferred_communication', 'Contact information and preferred communication methods')}</li>
            </ul>

            <h3>3. Who May Disclose This Information</h3>
            <p>
              {t('hipaa-authorization.the_following_entities_are_authorized', 'The following entities are authorized to disclose your PHI:')}
            </p>
            <ul>
              <li>{t('hipaa-authorization.trustcare_and_its_employees_contractors', 'Harmony Healthcare Solutions and its employees, contractors, and agents')}</li>
              <li>{t('hipaa-authorization.healthcare_providers_participating_in_your', 'Healthcare providers participating in your care coordination')}</li>
              <li>{t('hipaa-authorization.communitybased_organizations_providing_services_to', 'Community-based organizations providing services to you')}</li>
              <li>{t('hipaa-authorization.health_plans_and_insurance_companies', 'Health plans and insurance companies involved in your care')}</li>
              <li>{t('hipaa-authorization.social_service_agencies_coordinating_services', 'Social service agencies coordinating services on your behalf')}</li>
            </ul>

            <h3>4. Who May Receive This Information</h3>
            <p>
              {t('hipaa-authorization.your_phi_may_be_disclosed', 'Your PHI may be disclosed to:')}
            </p>
            <ul>
              <li>{t('hipaa-authorization.your_assigned_case_manager_and', 'Your assigned case manager and care coordinators')}</li>
              <li>{t('hipaa-authorization.healthcare_providers_involved_in_your', 'Healthcare providers involved in your treatment')}</li>
              <li>{t('hipaa-authorization.social_service_providers_delivering_services', 'Social service providers delivering services to you')}</li>
              <li>{t('hipaa-authorization.health_plans_and_insurance_companies_1', 'Health plans and insurance companies for coverage and payment purposes')}</li>
              <li>{t('hipaa-authorization.other_individuals_or_organizations_specifically', 'Other individuals or organizations specifically authorized by you')}</li>
              <li>{t('hipaa-authorization.government_agencies_as_required_by', 'Government agencies as required by law')}</li>
            </ul>

            <h3>5. Purpose of Use and Disclosure</h3>
            <p>
              {t('hipaa-authorization.your_phi_will_be_used', 'Your PHI will be used and disclosed for the following purposes:')}
            </p>
            <ul>
              <li>{t('hipaa-authorization.coordinating_your_healthcare_and_social', 'Coordinating your healthcare and social services')}</li>
              <li>{t('hipaa-authorization.facilitating_communication_between_your_care', 'Facilitating communication between your care team members')}</li>
              <li>{t('hipaa-authorization.assessing_your_eligibility_for_programs', 'Assessing your eligibility for programs and services')}</li>
              <li>{t('hipaa-authorization.monitoring_your_progress_and_outcomes', 'Monitoring your progress and outcomes')}</li>
              <li>{t('hipaa-authorization.processing_insurance_claims_and_obtaining', 'Processing insurance claims and obtaining payment for services')}</li>
              <li>{t('hipaa-authorization.complying_with_legal_and_regulatory', 'Complying with legal and regulatory requirements')}</li>
              <li>{t('hipaa-authorization.quality_improvement_and_program_evaluation', 'Quality improvement and program evaluation')}</li>
              <li>{t('hipaa-authorization.providing_you_with_information_about', 'Providing you with information about your care through the Member Portal')}</li>
            </ul>

            <h3>6. Your Rights</h3>
            <p>
              {t('hipaa-authorization.you_have_the_following_rights', 'You have the following rights regarding this authorization:')}
            </p>

            <h4>{t('hipaa-authorization.right_to_revoke', 'Right to Revoke')}</h4>
            <p>
              {t('hipaa-authorization.you_may_revoke_this_authorization', 'You may revoke this authorization at any time by submitting a written request to Harmony Healthcare Solutions. If you revoke this authorization:')}
            </p>
            <ul>
              <li>{t('hipaa-authorization.the_revocation_will_not_affect', 'The revocation will not affect any actions taken before we received your revocation')}</li>
              <li>{t('hipaa-authorization.we_may_not_be_able', 'We may not be able to continue providing care coordination services to you')}</li>
              <li>{t('hipaa-authorization.you_may_not_be_able', 'You may not be able to access certain features of the Member Portal')}</li>
            </ul>

            <h4>{t('hipaa-authorization.right_to_refuse_to_sign', 'Right to Refuse to Sign')}</h4>
            <p>
              {t('hipaa-authorization.you_do_not_have_to', 'You do not have to sign this authorization. However, if you do not sign:')}
            </p>
            <ul>
              <li>{t('hipaa-authorization.we_may_not_be_able_1', 'We may not be able to provide care coordination services to you')}</li>
              <li>{t('hipaa-authorization.you_may_not_be_able_1', 'You may not be able to access the Member Portal')}</li>
              <li>{t('hipaa-authorization.your_ability_to_receive_coordinated', 'Your ability to receive coordinated care from multiple providers may be limited')}</li>
            </ul>

            <h4>{t('hipaa-authorization.right_to_receive_a_copy', 'Right to Receive a Copy')}</h4>
            <p>
              {t('hipaa-authorization.you_have_the_right_to', 'You have the right to receive a copy of this authorization for your records.')}
            </p>

            <h3>7. Re-disclosure</h3>
            <p>
              We will make reasonable efforts to ensure that persons or entities receiving your PHI under this authorization will not re-disclose it. However, once information is disclosed pursuant to this authorization, federal privacy laws may no longer protect it, and the recipient may re-disclose it.
            </p>

            <h3>8. Expiration</h3>
            <p>
              {t('hipaa-authorization.this_authorization_will_remain_in', 'This authorization will remain in effect for the duration of your participation in care coordination services, unless:')}
            </p>
            <ul>
              <li>{t('hipaa-authorization.you_revoke_it_in_writing', 'You revoke it in writing')}</li>
              <li>{t('hipaa-authorization.your_participation_in_care_coordination', 'Your participation in care coordination services ends')}</li>
              <li>{t('hipaa-authorization.we_notify_you_that_we', 'We notify you that we are terminating our services')}</li>
            </ul>

            <h3>9. Sensitive Information</h3>
            <p>
              {t('hipaa-authorization.this_authorization_includes_the_release', 'This authorization includes the release of information related to:')}
            </p>
            <ul>
              <li>{t('hipaa-authorization.mental_health_treatment', 'Mental health treatment')}</li>
              <li>{t('hipaa-authorization.drug_and_alcohol_abuse_treatment', 'Drug and alcohol abuse treatment')}</li>
              <li>{t('hipaa-authorization.hivaids_testing_and_treatment', 'HIV/AIDS testing and treatment')}</li>
              <li>{t('hipaa-authorization.genetic_testing', 'Genetic testing')}</li>
              <li>{t('hipaa-authorization.sexually_transmitted_diseases', 'Sexually transmitted diseases')}</li>
            </ul>
            <p>
              {t('hipaa-authorization.federal_and_state_laws_provide', 'Federal and state laws provide special protections for this type of information, and re-disclosure by recipients may be prohibited without your specific written consent.')}
            </p>

            <h3>10. No Conditioning of Treatment</h3>
            <p>
              {t('hipaa-authorization.your_treatment_payment_enrollment_in', 'Your treatment, payment, enrollment in a health plan, or eligibility for benefits will not be conditioned on whether you sign this authorization, except as permitted by law.')}
            </p>

            <h3>11. Questions and Concerns</h3>
            <p>
              {t('hipaa-authorization.if_you_have_questions_about', 'If you have questions about this authorization or concerns about the privacy of your health information, you may contact:')}
            </p>
            <ul>
              <li><strong>{t('hipaa-authorization.trustcare_privacy_officer', 'Harmony Healthcare Solutions Privacy Officer')}</strong></li>
              <li>{t('hipaa-authorization.email', 'Email:')} <a href="mailto:support@weavein.ai" className="text-teal-600">support@weavein.ai</a></li>
              <li>Mail: Harmony Healthcare Solutions, Inc., 5776 Stoneridge Mall Road, Suite 125, Pleasanton, CA 94544</li>
            </ul>

            <h3>12. Filing a Complaint</h3>
            <p>
              {t('hipaa-authorization.if_you_believe_your_privacy', 'If you believe your privacy rights have been violated, you may file a complaint with:')}
            </p>
            <ul>
              <li>{t('hipaa-authorization.trustcare_privacy_officer_at_the', 'Harmony Healthcare Solutions Privacy Officer at the contact information above')}</li>
              <li>{t('hipaa-authorization.the_us_department_of_health', 'The U.S. Department of Health and Human Services Office for Civil Rights')}</li>
            </ul>
            <p>
              {t('hipaa-authorization.you_will_not_be_retaliated', 'You will not be retaliated against for filing a complaint.')}
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mt-8">
              <p className="font-semibold text-blue-900">{t('hipaa-authorization.notice_to_member', 'Notice to Member')}</p>
              <p className="text-sm text-blue-800 mt-2">
                {t('hipaa-authorization.by_signing_this_authorization_during', 'By signing this authorization during registration, you acknowledge that:')}
              </p>
              <ul className="text-sm text-blue-800 mt-2 space-y-1">
                <li>• You have read and understood this authorization</li>
                <li>• You have been given an opportunity to ask questions</li>
                <li>• You authorize the use and disclosure of your PHI as described above</li>
                <li>• You understand your rights and the consequences of signing or refusing to sign</li>
              </ul>
            </div>

            <div className="text-center mt-8 pt-6 border-t">
              <p className="text-sm text-gray-600">
                © {currentYear} Harmony Healthcare Solutions, Inc. All rights reserved.
              </p>
              <p className="text-xs text-gray-500 mt-1">
                HARMONY HEALTH DOES NOT PROVIDE MEDICAL SERVICES. IF YOU REQUIRE MEDICAL ATTENTION, PLEASE CALL YOUR REGULAR DOCTOR.
              </p>
              <p className="text-xs text-gray-500 mt-2">
                This authorization complies with the Health Insurance Portability and Accountability Act (HIPAA) and applicable state privacy laws.
              </p>
            </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
